import { sellerQueries } from '@/api';
import ProfileIcon from '@/assets/icons/More/IconUserprofile.svg?react';
import { Button } from '@/components/atoms/PcButton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/atoms/PcTooltip';
import { Separator } from '@/components/atoms/Seperator';
import { useAppSelector } from '@/redux/store';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { AlertProvider } from './AlertProvider';
import { ConfigProvider, useConfig } from './ConfigProvider';
import { ConnectedProvider } from './ConnectedProvider';
import { NotificationReadProvider, useNotificationRead } from './NotificationReadContext';
import { UserConfigItem } from './UserConfigItem';
import { ROUTES } from '@/routes';
import { useMembershipPreviewAccess } from '@/hooks/useMembershipPreviewAccess';
const UserConfig = () => {
  return (
    <ConfigProvider>
      <NotificationReadProvider>
        <UserConfigContent />
      </NotificationReadProvider>
    </ConfigProvider>
  );
};

export default UserConfig;

const UserConfigContent = () => {
  const { notificationList, registedMarketList } = useConfig();
  const { readSeqNos } = useNotificationRead();
  const isMembershipPreview = useMembershipPreviewAccess();

  const unreadCount =
    notificationList.length === 0
      ? 0
      : notificationList.filter((n) => !readSeqNos.includes(n.SeqNo)).length;

  const USER_CONFIG = [
    {
      id: 'membership',
      title: '멤버십',
      linkTo: ROUTES.moreMembership,
    },
    {
      id: 'newAlert',
      title: '신규 알림',
      popoverComponent: AlertProvider,
      count: isMembershipPreview ? 0 : unreadCount,
    },
    {
      id: 'connectedMarket',
      title: '연동 중인 마켓',
      popoverComponent: ConnectedProvider,

      count: registedMarketList.length,
    },
  ];

  return (
    <ConfigProvider>
      <div className="justify flex h-full w-fit items-center gap-10 py-[24px]">
        <div className="flex h-full items-center gap-[15px]">
          {USER_CONFIG.map((item, idx) => (
            <div key={item.id} className="flex h-full items-center gap-[15px]">
              <UserConfigItem {...item} />
              {idx < USER_CONFIG.length - 1 && (
                <Separator orientation="vertical" className="bg-gr-300" />
              )}
            </div>
          ))}
        </div>
        <UserNameConfig />
      </div>
    </ConfigProvider>
  );
};

const UserNameConfig = () => {
  const user = useAppSelector((state) => state.user);

  const navigate = useNavigate();
  const { data } = useQuery(sellerQueries.getInfo({ cplatToken: user.CplatToken }));
  const sellerImageUrl = data?.data?.[0].sellerImageUrl;
  const userName = data?.data?.[0].name;

  const [, setLocalStorageCplatToken] = useLocalStorage('cplatToken', '');

  // 로그아웃 함수
  const handleLogout = () => {
    window.location.href = ROUTES.root; // 외부 페이지로 이동
    setLocalStorageCplatToken(''); // 토큰 삭제
    localStorage.removeItem('cplatToken'); // 혹시 모르니 직접 삭제도 추가
  };

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="flex cursor-pointer items-center gap-[10px]"
            tabIndex={0}
            role="button"
            aria-label="마이페이지로 이동"
            onClick={() => navigate(ROUTES.more)}
          >
            {/* 프로필 이미지 또는 회색 동그라미 */}
            {sellerImageUrl ? (
              <img
                src={sellerImageUrl}
                alt={user.StoreName || '프로필'}
                className="h-[32px] w-[32px] rounded-full border bg-white object-cover object-center"
              />
            ) : (
              <div className="flex h-[32px] w-[32px] items-center justify-center rounded-full border border-gr-300 bg-white">
                <ProfileIcon width={20} height={20} />
              </div>
            )}
            {/* 상점 이름 */}
            <span className="max-w-[150px] truncate text-gr-900 Body5M14">{userName}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent sideOffset={0} side="bottom" align="center" className="bg-transparent p-0">
          <Button
            variant="outline"
            className="w-[100px] justify-start p-3 text-left text-gr-700 Body5M14"
            onClick={handleLogout}
          >
            로그아웃
          </Button>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
